import type { FieldConfig } from '../ForgeQueryBuilder/types';
import type { MainBody } from '../../services/manager/main-body';
import type { Shop } from '../../types/entity/shop';

// 单选：按主体选择，实际返回该主体下的店铺ID数组（用于 in 查询）
export function mainBodyStoreIdsSingleSelect(mainBodies: MainBody[], shops: Shop[], group?: string): FieldConfig {
  return {
    key: 'storeId',
    group,
    label: '主体',
    type: 'select',
    operators: ['in'],
    getOptions: () => {
      const allStoreIds = shops.map((s) => Number(s.id));
      return [
        { label: '全部', value: '', queryValue: { operator: 'in', value: allStoreIds } },
        ...mainBodies.map((b) => {
          const mbId = Number((b as any).id);
          const relatedStoreIds = shops
            .filter((s) => Number(s.main_body_id) === mbId)
            .map((s) => Number(s.code));
          return {
            label: String(b.name ?? b.code ?? b.id ?? ''),
            value: mbId,
            queryValue: { operator: 'in', value: relatedStoreIds },
          };
        }),
      ];
    },
  };
}

export function mainBodyStoreUnionSingleSelect(mainBodies: MainBody[], shops: Shop[], group?: string): FieldConfig {
  return {
    key: 'storeId',
    group,
    label: '主体/店铺',
    type: 'cascader',
    operators: ['eq'],
    placeholder: '请选择主体或店铺',
    getCascaderOptions: () => {
      return mainBodies.map((b) => {
        const mbId = Number((b as any).id);
        const relatedStoreCodes = shops
          .filter((s) => Number(s.main_body_id) === mbId)
          .map((s) => Number(s.code || s.id));
        const children = [
          { label: '全部', value: 'all', queryValue: { operator: 'in', value: relatedStoreCodes } },
          ...shops
            .filter((s) => Number(s.main_body_id) === mbId)
            .map((s) => ({
              label: String(s.name ?? s.code ?? s.id ?? ''),
              value: Number(s.id),
              queryValue: { operator: 'eq', value: Number(s.code || s.id) },
            })),
        ];
        return {
          label: String(b.name ?? b.code ?? b.id ?? ''),
          value: mbId,
          children,
        } as any;
      });
    },
  };
}

export function mainBodyStoreUnionMultiSelect(mainBodies: MainBody[], shops: Shop[], key: string = 'store_id', group?: string): FieldConfig {
  return {
    key,
    group,
    label: '主体/店铺',
    type: 'cascader',
    operators: ['in'],
    placeholder: '请选择主体或店铺',
    width: 200,
    cascaderProps: {
      multiple: true,
      maxTagCount: 'responsive',
      displayRender: (labels: string[]) => labels.join('/'),
      changeOnSelect: true,
      showCheckedStrategy: 'SHOW_CHILD',
    },
    onValueChange: (val, values) => {
      const nextPaths: any[][] = Array.isArray(val) ? (val as any[][]) : [];
      const prevPaths: any[][] = Array.isArray(values?.[key]) ? (values[key] as any[][]) : [];

      const dedup = (paths: any[][]) => {
        const map = new Map<string, any[]>();
        paths.forEach((p) => {
          if (!Array.isArray(p) || p.length === 0) return;
          const k = p.map((x) => String(x)).join('||');
          if (!map.has(k)) map.set(k, p);
        });
        return Array.from(map.values());
      };

      const isParentPath = (p: any[]) => Array.isArray(p) && p.length === 1;
      const parentBodyIds = new Set(nextPaths.filter(isParentPath).map((p) => String(p[0])));
      const leafPaths = nextPaths.filter((p) => Array.isArray(p) && p.length >= 2);

      const expandedFromParents: any[][] = [];
      parentBodyIds.forEach((bodyId) => {
        const relatedStores = shops.filter((s) => String(s.main_body_id) === bodyId);
        relatedStores.forEach((s) => expandedFromParents.push([bodyId, s.code || s.id]));
      });

      const merged = dedup([...leafPaths, ...expandedFromParents]);
      if (merged.length === 0 && prevPaths.length === 0) return { [key]: [] };
      return { [key]: merged };
    },
    getCascaderOptions: () => {
      return mainBodies.map((b) => {
        const mbId = String((b as any).id);
        const relatedStores = shops.filter((s) => String(s.main_body_id) === mbId);
        const children = relatedStores.map((s) => ({
          label: String(s.name ?? s.code ?? s.id ?? ''),
          value: String(s.code || s.id),
          queryValue: { operator: 'eq', value: Number(s.code || s.id) },
        }));
        return {
          label: String(b.name ?? b.code ?? b.id ?? ''),
          value: mbId,
          children,
        } as any;
      });
    },
  };
}

/**
 * 品牌/型号级联查询：UI 使用 Cascader 分组展示品牌，实际只对 `product_sku` 做查询。
 */
export function brandModelLinkedFields(
  brands: { key: string; name: string }[],
  skus: { key: string; name: string; parentKey?: string }[],
  group?: string
): FieldConfig[] {
  const brandMap = new Map<string, { key: string; name: string }>();
  brands.forEach((b) => {
    if (!b.key) return;
    brandMap.set(b.key, b);
  });

  const normalize = (v: unknown) => String(v ?? '').trim().toLowerCase();
  const skuBelongsToBrand = (sku: { parentKey?: string }, brandKey: string) => {
    const brand = brandMap.get(brandKey);
    const pk = normalize(sku.parentKey);
    if (!pk) return false;
    if (pk === normalize(brandKey)) return true;
    if (brand?.name && pk === normalize(brand.name)) return true;
    return false;
  };

  // const unknownBrandModels = skus.filter((m) => !m.parentKey || !brandMap.has(m.parentKey));

  const field: FieldConfig = {
    key: 'product_sku',
    group,
    label: '品牌/型号',
    type: 'cascader',
    operators: ['in'],
    placeholder: '请选择品牌/型号',
    width: 200,
    cascaderProps: {
      multiple: true,
      maxTagCount: 'responsive',
      displayRender: (labels: string[]) => labels.join('/'),
      changeOnSelect: true,
      showCheckedStrategy: 'SHOW_CHILD',
    },
    onValueChange: (val, values) => {
      const nextPaths: any[][] = Array.isArray(val) ? (val as any[][]) : [];
      const prevPaths: any[][] = Array.isArray(values?.product_sku) ? (values.product_sku as any[][]) : [];

      const dedup = (paths: any[][]) => {
        const map = new Map<string, any[]>();
        paths.forEach((p) => {
          if (!Array.isArray(p) || p.length === 0) return;
          const k = p.map((x) => String(x)).join('||');
          if (!map.has(k)) map.set(k, p);
        });
        return Array.from(map.values());
      };

      const isParentPath = (p: any[]) => Array.isArray(p) && p.length === 1;
      const parentBrandIds = new Set(nextPaths.filter(isParentPath).map((p) => String(p[0])));
      const leafPaths = nextPaths.filter((p) => Array.isArray(p) && p.length >= 2);

      const expandedFromParents: any[][] = [];
      parentBrandIds.forEach((brandId) => {
        const relatedModels = skus.filter((m) => skuBelongsToBrand(m, brandId));
        relatedModels.forEach((m) => expandedFromParents.push([brandId, m.key || m.name]));
      });

      const merged = dedup([...leafPaths, ...expandedFromParents]);
      if (merged.length === 0 && prevPaths.length === 0) return { product_sku: [] };
      return { product_sku: merged };
    },
    getCascaderOptions: () => {
      const buildBrandChildren = (brandId: string) => {
        const relatedModels = skus.filter((m) => skuBelongsToBrand(m, brandId));
        return [
          ...relatedModels.map((m) => ({
            label: m.name,
            value: m.key || m.name,
            queryValue: { operator: 'eq', value: m.name },
          })),
        ];
      };

      const opts = brands
        .filter((b) => b.key)
        .map((b) => ({
          label: b.name,
          value: b.key,
          children: buildBrandChildren(b.key),
        }));

      return opts as any;
    },
  };

  return [field];
}
